'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useI18n } from '@/lib/i18n'
import { gameQueries } from '@/lib/queries'
import { RefreshCw, Save } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

type PoolConfig = Record<string, string[]>

export function GameModelPoolSection() {
  const { t } = useI18n()
  const [modelConfig, setModelConfig] = useState<Record<string, string[]>>({})
  const [lowCost, setLowCost] = useState<PoolConfig>({})
  const [highPerformance, setHighPerformance] = useState<PoolConfig>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [saveMessage, setSaveMessage] = useState<'success' | 'error' | null>(null)

  const providers = Object.keys(modelConfig)

  const loadPools = useCallback(async () => {
    setLoading(true)
    try {
      const [configResult, poolsResult] = await Promise.all([
        gameQueries.getModelConfig(),
        gameQueries.getGameModelPools(),
      ])
      if (configResult.success && configResult.data)
        setModelConfig(configResult.data)
      if (poolsResult.success && poolsResult.data) {
        setLowCost(poolsResult.data.lowCost ?? {})
        setHighPerformance(poolsResult.data.highPerformance ?? {})
      }
    }
    catch (error) {
      console.error('Failed to load game model pools:', error)
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPools()
  }, [loadPools])

  const handleSave = async () => {
    setSaving(true)
    setSaveMessage(null)
    try {
      await gameQueries.setGameModelPools({ lowCost, highPerformance })
      setSaveMessage('success')
    }
    catch (error) {
      console.error('Failed to save game model pools:', error)
      setSaveMessage('error')
    }
    finally {
      setSaving(false)
    }
  }

  const handleRefreshResponsiveModels = async () => {
    setRefreshing(true)
    try {
      await gameQueries.refreshResponsiveModelsCache()
    }
    catch (error) {
      console.error('Failed to refresh responsive models cache:', error)
    }
    finally {
      setRefreshing(false)
    }
  }

  const toggleLowCost = (model: string, checked: boolean) => {
    const provider = providers.find(p => (modelConfig[p] ?? []).includes(model))
    if (!provider)
      return
    const current = lowCost[provider] ?? []
    const next = checked ? [...current, model] : current.filter(m => m !== model)
    setLowCost(prev => ({ ...prev, [provider]: next }))
  }

  const toggleHighPerformance = (model: string, checked: boolean) => {
    const provider = providers.find(p => (modelConfig[p] ?? []).includes(model))
    if (!provider)
      return
    const current = highPerformance[provider] ?? []
    const next = checked ? [...current, model] : current.filter(m => m !== model)
    setHighPerformance(prev => ({ ...prev, [provider]: next }))
  }

  const allModelsPool: PoolConfig = providers.reduce(
    (acc, p) => {
      acc[p] = [...(modelConfig[p] ?? [])]
      return acc
    },
    {} as PoolConfig,
  )

  const handleLowCostSelectAll = () => setLowCost(allModelsPool)
  const handleLowCostUnselectAll = () => setLowCost({})
  const handleHighPerformanceSelectAll = () => setHighPerformance(allModelsPool)
  const handleHighPerformanceUnselectAll = () => setHighPerformance({})

  if (loading) {
    return (
      <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl text-center text-muted-foreground">
          {t('admin.gameModelPool.loading', 'Loading pools...')}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.gameModelPool.title', 'Game Model Pools')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.gameModelPool.description', 'Configure which models are in the low-cost and high-performance pools. Save then run Calculate Responsive Model to refresh the cache.')}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw className="size-4 animate-spin" />
                  {t('admin.gameModelPool.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="size-4" />
                  {t('admin.gameModelPool.savePools', 'Save pools')}
                </>
              )}
            </Button>
            <Button
              onClick={handleRefreshResponsiveModels}
              disabled={refreshing}
              variant="outline"
              className="flex items-center gap-2"
            >
              {refreshing ? (
                <>
                  <RefreshCw className="size-4 animate-spin" />
                  {t('admin.gameModelPool.calculating', 'Calculating...')}
                </>
              ) : (
                <>
                  <RefreshCw className="size-4" />
                  {t('admin.gameModelPool.calculateResponsive', 'Calculate Responsive Model')}
                </>
              )}
            </Button>
            {saveMessage === 'success' && (
              <span className="text-sm text-green-600 dark:text-green-400">
                {t('admin.gameModelPool.saved', 'Pools saved.')}
              </span>
            )}
            {saveMessage === 'error' && (
              <span className="text-sm text-destructive">
                {t('admin.gameModelPool.saveError', 'Failed to save.')}
              </span>
            )}
          </div>

          <p className="-mt-2 text-xs text-muted-foreground">
            {t('admin.gameModelPool.responsiveHint', 'Calculate Responsive Model only probes the checked pool models above (low-cost + high-performance) — not every available model. Uncheck a model to exclude it from the probe and save tokens.')}
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>
                    {t('admin.gameModelPool.lowCostTitle', 'Low-cost pool')}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleLowCostSelectAll}
                    >
                      {t('admin.gameModelPool.selectAll', 'Select all')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleLowCostUnselectAll}
                    >
                      {t('admin.gameModelPool.unselectAll', 'Unselect all')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {providers.map((provider) => {
                  const models = modelConfig[provider] ?? []
                  const selected = new Set(lowCost[provider] ?? [])
                  return (
                    <div key={provider} className="space-y-2">
                      <h4 className="text-sm font-medium capitalize text-foreground">
                        {provider}
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {models.map(model => (
                          <label
                            key={model}
                            className="flex cursor-pointer items-center gap-2 text-sm"
                          >
                            <Checkbox
                              checked={selected.has(model)}
                              onCheckedChange={checked => toggleLowCost(model, !!checked)}
                            />
                            <span className="font-mono text-muted-foreground">
                              {model.split('/').pop() ?? model}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>
                    {t('admin.gameModelPool.highPerformanceTitle', 'High-performance pool')}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleHighPerformanceSelectAll}
                    >
                      {t('admin.gameModelPool.selectAll', 'Select all')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleHighPerformanceUnselectAll}
                    >
                      {t('admin.gameModelPool.unselectAll', 'Unselect all')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {providers.map((provider) => {
                  const models = modelConfig[provider] ?? []
                  const selected = new Set(highPerformance[provider] ?? [])
                  return (
                    <div key={provider} className="space-y-2">
                      <h4 className="text-sm font-medium capitalize text-foreground">
                        {provider}
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {models.map(model => (
                          <label
                            key={model}
                            className="flex cursor-pointer items-center gap-2 text-sm"
                          >
                            <Checkbox
                              checked={selected.has(model)}
                              onCheckedChange={checked => toggleHighPerformance(model, !!checked)}
                            />
                            <span className="font-mono text-muted-foreground">
                              {model.split('/').pop() ?? model}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
